import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history, request as umiRequest } from '@umijs/max';
import { message } from 'antd';
import {
  clearAdminToken,
  getAdminRefreshToken,
  refreshAdminToken,
  setAdminToken,
} from '@/services/admin-auth';
import {
  extractErrorMessage,
  showErrorNotification,
} from '@/utils/requestError';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
// interface ResponseStructure {
//   code: number; // 后端实际返回的是 code
//   message: string; // 后端实际返回的是 message
//   data: any;
//   timestamp?: number;
//   showType?: ErrorShowType;
// }

// 防止并发刷新 token
let adminRefreshPromise: Promise<string | null> | null = null;

const resolveRequestUrl = (error: any, opts: any): string => {
  const candidates = [
    opts?.url,
    opts?.request?.url,
    error?.info?.requestUrl,
    error?.config?.url,
    error?.response?.config?.url,
    error?.request?.responseURL,
  ];
  return candidates.find(
    (value) => typeof value === 'string' && value.length > 0,
  ) || '';
};

const toRequestPath = (url: string): string => {
  if (!url) return '';
  try {
    const base =
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    return url;
  }
};

const refreshAdminAccessToken = async (): Promise<string | null> => {
  if (adminRefreshPromise) return adminRefreshPromise;

  const refreshToken = getAdminRefreshToken();
  if (!refreshToken) return null;

  adminRefreshPromise = (async () => {
    try {
      const response = await refreshAdminToken(refreshToken);
      const data = (response as any)?.data || response;
      setAdminToken(data.accessToken, data.refreshToken);
      return data.accessToken;
    } catch (_error) {
      clearAdminToken();
      return null;
    } finally {
      adminRefreshPromise = null;
    }
  })();

  return adminRefreshPromise;
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const resObj = res as unknown as any;

      // @umijs/max 会自动解包响应数据
      // 如果响应包含 code 字段，说明是完整的 ResponseStructure
      // 如果没有 code 字段，说明已经被自动解包成了 data 对象
      const code = resObj?.code;
      const message = resObj?.message;
      const data = resObj?.data;
      const showType = resObj?.showType;

      // 如果没有 code，说明这就是解包后的 data，不需要抛出错误
      if (code === undefined) {
        // 这是已解包的响应，正常处理，不抛出错误
        return;
      }

      // 后端约定：code=200 表示成功，其他表示失败
      if (code !== 200) {
        const error: any = new Error(message);
        error.name = 'BizError';
        error.info = {
          errorCode: code,
          errorMessage: message,
          showType,
          data,
          requestUrl: resObj?.__requestUrl,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: async (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 处理管理员 token 过期的情况
      const requestUrl = resolveRequestUrl(error, opts);
      const requestPath = toRequestPath(requestUrl);
      const isAdminRefresh =
        requestPath.startsWith('/admin/auth/refresh') ||
        requestPath.startsWith('/api/admin/auth/refresh');
      const isUnauthorized =
        error?.info?.errorCode === 401 || error?.response?.status === 401;

      if (!isAdminRefresh && isUnauthorized) {
        const newToken = await refreshAdminAccessToken();
        if (newToken && requestUrl) {
          // 用新 token 重试请求
          const retryConfig = { ...opts };
          retryConfig.headers = retryConfig.headers || {};
          retryConfig.headers.Authorization = `Bearer ${newToken}`;
          // 使用 request 重新发送请求
          return umiRequest(requestUrl, { ...retryConfig, skipErrorHandler: true });
        } else {
          // token 刷新失败，清空存储并重定向
          clearAdminToken();
          history.push('/user/login');
          throw error;
        }
      }

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: any = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              showErrorNotification(
                error,
                errorMessage,
                errorCode ? `错误码: ${errorCode}` : '请求失败',
              );
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        showErrorNotification(
          error,
          `请求失败: HTTP ${error.response.status}`,
          '请求失败',
        );
      } else if (error.request) {
        showErrorNotification(error, '服务器无响应，请稍后重试');
      } else {
        showErrorNotification(
          error,
          extractErrorMessage(error, '请求失败，请检查网络连接'),
        );
      }
      return undefined;
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      // 支持双认证系统：管理员 token 和用户 token
      // 管理端路径：/admin/** 或 /api/admin/** 或其他管理端接口（如 /api/platform/** 等）
      const isAdminPath =
        config.url?.startsWith('/admin') ||
        config.url?.startsWith('/api/admin') ||
        config.url?.includes('-management');

      // 调试日志
      console.log(
        '[RequestInterceptor] URL:',
        config.url,
        'isAdminPath:',
        isAdminPath,
      );

      // 检查是否已有授权头（例如 refresh 请求可能已设置）
      const existingAuth = (config.headers as Record<string, any>)
        ?.Authorization;
      const hasAuthHeader =
        typeof existingAuth === 'string' && existingAuth.length > 0;

      console.log(
        '[RequestInterceptor] Existing Authorization:',
        !!hasAuthHeader,
      );

      if (!hasAuthHeader) {
        // 优先检查是否有管理员 token（在管理端应用中优先使用）
        const adminToken = localStorage.getItem('admin_token');
        const userToken = localStorage.getItem('token');

        console.log(
          '[RequestInterceptor] Available tokens - admin:',
          !!adminToken,
          'user:',
          !!userToken,
        );

        if (adminToken) {
          // 如果有管理员 token，优先使用（管理端应该总是有）
          config.headers = config.headers || {};
          (config.headers as Record<string, any>).Authorization =
            `Bearer ${adminToken}`;
          console.log(
            '[RequestInterceptor] ✓ Using admin token (from admin_token)',
          );
        } else if (userToken) {
          // 否则使用用户 token
          config.headers = config.headers || {};
          (config.headers as Record<string, any>).Authorization =
            `Bearer ${userToken}`;
          console.log('[RequestInterceptor] ✓ Using user token (from token)');
        } else {
          console.log('[RequestInterceptor] ⚠ No tokens found in localStorage');
        }
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      // 后端返回格式: { code, message, data }
      // 不需要额外处理，errorThrower 会处理错误情况
      const payload = (response as any)?.data;
      const requestUrl = (response as any)?.config?.url;
      if (payload && typeof payload === 'object' && requestUrl) {
        Object.defineProperty(payload, '__requestUrl', {
          configurable: true,
          enumerable: false,
          value: requestUrl,
        });
      }
      return response;
    },
  ],
};
